'use strict';


import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';
import {
	Color,
	DepthTexture,
	MeshBasicMaterial,
	NoBlending,
	ShaderMaterial,
	UniformsUtils,
	WebGLRenderTarget,
} from 'three';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import vertex from './Shaders/MaskObject/vertex.glsl';
import fragment from './Shaders/MaskObject/fragment.glsl';
import { Effect } from './Effect';
import { SSAODepthShader } from 'three/examples/jsm/shaders/SSAOShader';


export class MaskObject extends Effect{
	constructor (camera,width,height) {
		super();

		this.effect = new MaskObjectPass(camera,width,height);
		this.active = false;
	}

	setObject(object) {
		this.effect.setObject(object);
	}

}

class MaskObjectPass extends Pass{
	constructor (camera,width,height) {
		super();

		this.camera = camera;
		this.width = width;
		this.height = height;
		this.object = null;

		this.defaultMaterial = new ShaderMaterial({
			uniforms: UniformsUtils.clone( CopyShader.uniforms ),
			vertexShader: CopyShader.vertexShader,
			fragmentShader: CopyShader.fragmentShader,
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});

		this.depthRenderMaterial = new ShaderMaterial( {
			defines: Object.assign( {}, SSAODepthShader.defines ),
			uniforms: UniformsUtils.clone( SSAODepthShader.uniforms ),
			vertexShader: SSAODepthShader.vertexShader,
			fragmentShader: SSAODepthShader.fragmentShader,
			blending: NoBlending
		});

		this.depthRenderMaterial.uniforms.cameraNear.value = this.camera.near;
		this.depthRenderMaterial.uniforms.cameraFar.value = this.camera.far;

		this.maskMaterial = new ShaderMaterial({
			uniforms: {
				tDiffuse: {
					value: null
				},
				tDiffuseDepth: {
					value: null
				},
				tMask: {
					value: null
				},
				tObjectDepth: {
					value: null
				}
			},
			vertexShader: vertex,
			fragmentShader: fragment,
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});


		const depthTexture = new DepthTexture();

		this.defaultRt = new WebGLRenderTarget(this.width,this.height);
		this.masktRt = new WebGLRenderTarget(this.width,this.height,{
			depthTexture: depthTexture
		});
		this.depthDiffuseRt = new WebGLRenderTarget(this.width,this.height);
		this.depthObjectRt = new WebGLRenderTarget(this.width,this.height);

		this.fsQuad = new FullScreenQuad(null);
		this.originalClearColor = new Color();
		this.visibilityCache = new Map();
		this.objectMaterial = new MeshBasicMaterial();
		this.needsSwap = false;
	}

	setObject(object) {
		this.object = object;
	}

	setSize( width, height) {
		this.width = width;
		this.height = height;
		this.defaultRt.setSize(width,height);
		this.masktRt.setSize(width,height);
		this.depthDiffuseRt.setSize(width,height);
		this.depthObjectRt.setSize(width,height);
	}

	render( renderer, writeBuffer, readBuffer) {
		this.depthRenderMaterial.uniforms.cameraNear.value = this.camera.near;
		this.depthRenderMaterial.uniforms.cameraFar.value = this.camera.far;

		//Render beauty
		this.defaultMaterial.uniforms.tDiffuse.value = readBuffer.texture;
		this.renderPass(renderer,this.defaultMaterial,this.defaultRt);

		//Render object
		if (this.object) {
			renderer.setRenderTarget(this.masktRt);
			this.overrideVisibility();
			renderer.render(this.object,this.camera);
			this.maskMaterial.uniforms[ 'tMask' ].value = this.masktRt.texture;
			this.maskMaterial.uniforms[ 'tObjectDepth' ].value = this.masktRt.depthTexture;

			renderer.setRenderTarget(null);
			this.restoreVisibility();
		}

		this.maskMaterial.uniforms[ 'tDiffuse' ].value = this.defaultRt.texture;
		this.maskMaterial.uniforms[ 'tDiffuseDepth' ].value = readBuffer.depthTexture;
		this.renderPass( renderer, this.maskMaterial, this.renderToScreen ? null : readBuffer );
	}

	overrideVisibility() {
		const cache = this.visibilityCache;

		this.object.traverse(el => {
			if (el.isMesh) {
				cache.set(el, el.material);
				el.material = this.objectMaterial;
			}
		})
	}

	restoreVisibility() {
		const cache = this.visibilityCache;

		this.object.traverse(el => {
			if (el.isMesh) {
				el.material = cache.get(el);
			}
		});

		cache.clear();
	}

	renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
		// save original state
		renderer.getClearColor( this.originalClearColor );
		const originalClearAlpha = renderer.getClearAlpha();
		const originalAutoClear = renderer.autoClear;

		renderer.setRenderTarget( renderTarget );

		// setup pass state
		renderer.autoClear = false;
		if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {

			renderer.setClearColor( clearColor );
			renderer.setClearAlpha( clearAlpha || 0.0 );
			renderer.clear();

		}

		this.fsQuad.material = passMaterial;
		this.fsQuad.render( renderer );

		// restore original state
		renderer.autoClear = originalAutoClear;
		renderer.setClearColor( this.originalClearColor );
		renderer.setClearAlpha( originalClearAlpha );
	}

}
